import { Signal, computed } from '@angular/core';
import {
  SchemaFn,
  SchemaPath,
  disabled,
  readonly,
  max,
  maxLength,
  min,
  minLength,
  required,
  requiredError,
} from '@angular/forms/signals';
import { ValidatorSpec } from './field.model';

export interface FieldRules {
  readOnly: boolean;
  validators: ValidatorSpec[];
}

export type FieldShape = 'scalar' | 'list';

export const NO_RULES: FieldRules = { readOnly: false, validators: [] };

const REQUIRED_NAMES = new Set(['required', 'requiredFile']);

export function fieldRulesSchema<T>(
  rules: Signal<FieldRules>,
  shape: FieldShape,
  locked: Signal<boolean> = computed(() => false)
): SchemaFn<T> {
  const isRequired = () =>
    rules().validators.some(v => REQUIRED_NAMES.has(v.name));
  const limit = (name: string) => () => {
    const spec = rules().validators.find(v => v.name === name);
    return spec ? Number(spec.args) : undefined;
  };

  return tree => {
    const path = tree as SchemaPath<T>;
    disabled(path, { when: () => rules().readOnly });
    readonly(path, { when: () => locked() });
    required(path, { when: () => isRequired() });
    if (shape === 'list') {
      // required() counts [] as a value so a list needs at least one item.
      minLength(
        path as SchemaPath<unknown[]>,
        () => (isRequired() ? 1 : undefined),
        {
          error: requiredError(),
        }
      );
    }
    min(path as SchemaPath<number | null>, limit('min'));
    max(path as SchemaPath<number | null>, limit('max'));
    maxLength(path as SchemaPath<string>, limit('maxLength'));
  };
}
